import { useState } from 'react';

import { notifications } from '@mantine/notifications';
import { CreateFileDto, CreateFolderDto, CreateLocationDto, CreateUserDto, FileDto, FileMetadataUploadResponseDto, FolderDto, GetSpaceAccessInfoDto, LocationDto, ProfileDto, UpdateFolderDto, UpdateLocationDto, UpdateSpaceAccessDto } from '@braoshzmvavz/picnube-common';
import { createUserWithEmailAndPassword, sendEmailVerification, signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'src/app/hooks';
import { selectCurrentSpace } from 'src/state/profile/profileSlice';
import { openEmailVerificationModal } from '../components/modal/confirm';
import { UpdateFolderAccessDto } from '../model/UpdateFolderAccessDto';
import { useAuth } from './useAuth';
import { SearchFilters } from 'src/model/SearchFilters';

export enum APIError {
    NOT_FOUND = 'NOT_FOUND',
    ACCESS_DENIED = 'ACCESS_DENIED',
}

const useApi = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const { auth, validateAuth } = useAuth();

    const currentSpace = useAppSelector(selectCurrentSpace);

    //const isDevEnv = process.env.NODE_ENV === 'development';
    //const API_BASE_URL = isDevEnv ? '' : `${process.env.REACT_APP_API_BASE_URL}/`
    const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`
    const API_PATH_PROFILE = API_BASE_URL + process.env.REACT_APP_API_PATH_PROFILE
    const API_PATH_FOLDERS = API_BASE_URL + process.env.REACT_APP_API_PATH_FOLDERS
    const API_PATH_UPLOAD = API_BASE_URL + process.env.REACT_APP_API_PATH_UPLOAD
    const API_PATH_FILES = API_BASE_URL + process.env.REACT_APP_API_PATH_FILES
    const API_PATH_LOCATIONS = API_BASE_URL + process.env.REACT_APP_API_PATH_LOCATIONS
    const API_PATH_USERS = API_BASE_URL + process.env.REACT_APP_API_PATH_USERS
    const API_PATH_SEARCH = API_BASE_URL + process.env.REACT_APP_API_PATH_SEARCH
    const API_PATH_SPACES = API_BASE_URL + process.env.REACT_APP_API_PATH_SPACES

    const buildHeaders = async () => {
        const token = await auth.currentUser.getIdToken();

        return {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        }
    }

    const apiRequest = async<T, R>(url: string, method: string, body?: T): Promise<R> => {
        if (!validateAuth()) return;

        const headers = await buildHeaders();

        setLoading(true);

        const response: Response = await fetch(url, {
            method: method,
            headers: headers,
            body: body ? JSON.stringify(body) : undefined
        });

        setLoading(false);

        if (!response.ok) {
            throw new Error((await response.json())?.message ?? 'Unknown error');
        }

        return response.json();
    }

    const fetchProfile = async (defaultSpaceId?: string): Promise<ProfileDto> => {
        if (!!defaultSpaceId) {
            return await apiRequest(`${API_PATH_PROFILE}?` + new URLSearchParams({
                spaceId: defaultSpaceId
            }), 'GET');
        }
        return await apiRequest(API_PATH_PROFILE, 'GET');
    }

    const fetchFiles = async (spaceId: string, folderId: string, page: number) => {
        return await apiRequest(`${API_PATH_FILES}/search?` + new URLSearchParams({
            spaceId: spaceId,
            folderId: folderId,
            page: '' + page,
        }), 'GET');
    }

    const globalSearch = async (searchFilters: SearchFilters, filesPage: number) => {
        return await apiRequest(`${API_PATH_SEARCH}/global?` + new URLSearchParams({
            spaceId: currentSpace.id,
            filesPage: '' + filesPage,
        }), 'POST', searchFilters);
    }

    const searchByLocation = async (locationId: string) => {
        return await apiRequest(`${API_PATH_SEARCH}/locations/${locationId}?` + new URLSearchParams({
            spaceId: currentSpace.id,
        }), 'GET');
    }

    const createLocation = async (location: CreateLocationDto) => {
        return await apiRequest(`${API_PATH_LOCATIONS}?` + new URLSearchParams({
            spaceId: currentSpace.id,
        }), 'POST', location);
    }

    const updateLocation = async (location: UpdateLocationDto) => {
        return await apiRequest(`${API_PATH_LOCATIONS}/${location.id}?` + new URLSearchParams({
            spaceId: currentSpace.id,
        }), 'PUT', location);
    }

    const fetchFolders = async (spaceId: string): Promise<FolderDto[]> => {
        return await apiRequest(`${API_PATH_FOLDERS}?` + new URLSearchParams({
            spaceId: spaceId
        }), 'GET');
    }

    const fetchLocations = async (spaceId: string): Promise<LocationDto[]> => {
        return await apiRequest(`${API_PATH_LOCATIONS}?` + new URLSearchParams({
            spaceId: spaceId
        }), 'GET');
    }

    const fetchSpaceFolderTree = async (spaceId: string): Promise<any[]> => {
        return await apiRequest(`${API_PATH_FOLDERS}/tree?` + new URLSearchParams({
            spaceId: spaceId
        }), 'GET');
    }

    const moveFolders = async (folderIds: string[], targetFolderId: string) => {
        return await apiRequest(`${API_PATH_FOLDERS}/move?` + new URLSearchParams({
            spaceId: currentSpace.id,
        }), 'POST', {
            folders: folderIds,
            targetFolderId: targetFolderId,
        });
    }

    const searchFiles = async (spaceId: string, page: number, pageSize: number = 30, filters: any = null): Promise<FileDto[]> => {
        let body: any = {
            spaceId: spaceId,
            page: '' + page,
            sort: 'name',
            pageSize: '' + pageSize,

        }
        if (filters != null) {
            body = {
                ...body,
                startDate: filters?.start?.toISOString(),
                endDate: filters?.end?.toISOString(),
            }
        }
        return await apiRequest(`${API_PATH_FILES}/search?` + new URLSearchParams(body), 'GET');
    }

    const findFoldersByIdList = async (folderIds: string[]) => {
        return await apiRequest(`${API_PATH_FOLDERS}/findByIdList?` + new URLSearchParams({
            spaceId: currentSpace.id,
        }), 'POST', folderIds);
    }

    const fetchFolder = async (folderId: string, includeFiles: boolean) => {
        try {
            return await apiRequest(`${API_PATH_FOLDERS}/${folderId}?` + new URLSearchParams({
                includeFiles: '' + includeFiles, spaceId: currentSpace.id
            }), 'GET');

        } catch (e) {
            console.error(e);

            if (e.message === 'Folder not found') {
                return APIError.NOT_FOUND;
            }
            if (e.message === 'Access denied') {
                return APIError.ACCESS_DENIED;
            }

            return null;

        }
    }

    const createFolder = async (folder: CreateFolderDto): Promise<FolderDto> => {
        return await apiRequest(`${API_PATH_FOLDERS}?` + new URLSearchParams({
            spaceId: currentSpace.id
        }), 'POST', folder);
    }

    const updateFolder = async (folder: UpdateFolderDto): Promise<FolderDto> => {
        return await apiRequest(`${API_PATH_FOLDERS}/${folder.id}?` + new URLSearchParams({
            spaceId: currentSpace.id
        }), 'PUT', folder);
    }

    const getSpaceAccessInfo = async (spaceId: string): Promise<GetSpaceAccessInfoDto> => {
        console.log("getSpaceAccessInfo", spaceId);
        return await apiRequest(`${API_PATH_SPACES}/${spaceId}/access`, 'GET');
    }

    const updateSpaceAccess = async (spaceId: string, request: UpdateSpaceAccessDto) => {
        console.log("updateSpaceAccess", spaceId, request);
        return await apiRequest(`${API_PATH_SPACES}/${spaceId}/access`, 'POST', request);
    }

    const deleteFolder = async (folderId: string) => {
        return await apiRequest(`${API_PATH_FOLDERS}/${folderId}?` + new URLSearchParams({
            spaceId: currentSpace.id
        }), 'DELETE');
    }

    const deleteFile = async (fileId: string) => {
        return await apiRequest(`${API_PATH_FILES}/${fileId}?` + new URLSearchParams({
            spaceId: currentSpace.id
        }), 'DELETE');;
    }

    const deleteLocation = async (locationId: string) => {
        return await apiRequest(`${API_PATH_LOCATIONS}/${locationId}?` + new URLSearchParams({
            spaceId: currentSpace.id
        }), 'DELETE');;
    }

    const fetchAlbumsBySpaceId = async (spaceId: string) => {
        return [
            {
                id: "1",
                name: "Vacaciones en Polonia",
                coverImageUrl: "https://picsum.photos/id/10/400/600",
            },
            {
                id: "2",
                name: "Album 2",
                coverImageUrl: "https://picsum.photos/id/11/400/600",
            },
            {
                id: "3",
                name: "Album 3",
                coverImageUrl: "https://picsum.photos/id/12/400/600",
            },
            {
                id: "4",
                name: "Album 4",
                coverImageUrl: "https://picsum.photos/id/13/400/600",
            }
        ]
        /*return await apiRequest(`${API_PATH_ALBUMS}?` + new URLSearchParams({
            spaceId: spaceId
        }), 'GET');*/
    }

    // TODO: Rename to uploadMetadata
    const uploadFiles = async (files: CreateFileDto[], folders: CreateFolderDto[]): Promise<FileMetadataUploadResponseDto> => {
        return await apiRequest(`${API_PATH_UPLOAD}?` + new URLSearchParams({
            spaceId: currentSpace.id
        }), 'POST', {
            files: files,
            folders: folders,
        })
    }

    const requestFilesUpload = async (filesUploadRequest: any) => {
        if (!validateAuth()) return;

        try {
            setLoading(true);

            const response: Response = await fetch(
                `${API_PATH_FILES}/upload-request`,
                {
                    method: 'POST',
                    headers: await buildHeaders(),
                    body: JSON.stringify(filesUploadRequest)
                }).catch(err => {
                    console.log(err);
                    return null;
                });

            if (!response?.ok) {
                throw new Error();
            }

            const resolvedData = await response.json();

            return resolvedData;

        } catch (e) {
            console.error(e);
            notifications.show({
                id: "error-unknown",
                color: "red",
                title: "Error while uploading data to the server",
                message: "Check your internet connection",
                autoClose: false,
            });
            //navigate('/login');
            return;

        } finally {
            setLoading(false);
        }
    }

    const createUser = async (createUserDto: CreateUserDto): Promise<any> => {
        return await apiRequest(API_PATH_USERS, 'POST', createUserDto)
    }

    const login = async (email: string, password: string): Promise<boolean> => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password)

            const idToken = await userCredential.user.getIdToken();
            if (idToken) {
                return true;
            } else {
                return false;
            }
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    // TODO: Deprecate
    const register = async (name: string, email: string, password: string) => {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        await sendEmailVerification(auth.currentUser)

        console.log('Email sent, user account created:', userCredential.user);

        openEmailVerificationModal(email);
    }

    return {
        fetchProfile,
        fetchFiles,
        fetchFolder,
        fetchFolders,
        fetchLocations,
        fetchSpaceFolderTree,
        createLocation,
        updateLocation,
        updateFolder,
        createFolder,
        findFoldersByIdList,
        uploadFiles,
        searchFiles,
        globalSearch,
        searchByLocation,
        requestFilesUpload,
        updateSpaceAccess,
        getSpaceAccessInfo,
        deleteFile,
        deleteFolder,
        deleteLocation,
        login,
        register,
        loading,
        createUser,
        moveFolders,
        fetchAlbumsBySpaceId,
    };
};

export default useApi;
